import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import Footer from "../../components/Client/Footer";
import { useAuth } from "../../context/AuthContext";
import { fetchMyCoupons } from "../../api/couponService";
import "../../components/Client/clientTheme.css";
import "./MyCoupons.css";

const DISCOUNT_TYPE = { 1: "Percentage", 2: "FixedAmount", 3: "FreeDelivery" };

function TagIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M20 12.5 12.5 20a1.5 1.5 0 0 1-2.12 0l-6.38-6.38a1.5 1.5 0 0 1 0-2.12L11.5 4H19a1 1 0 0 1 1 1v7.5z" />
      <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function formatDiscount(coupon, t) {
  const type = DISCOUNT_TYPE[coupon.discountType];
  if (type === "Percentage") return t("discountPercentageOff", { value: coupon.discountValue });
  if (type === "FixedAmount") return t("discountFixedAmountOff", { value: coupon.discountValue });
  if (type === "FreeDelivery") return t("discountFreeDelivery");
  return t("discountGeneric");
}

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function MyCoupons() {
  const { t } = useTranslation(["myCoupons", "common"]);
  const { isAuthenticated, openLoginModal } = useAuth();
  const [coupons, setCoupons] = useState(null);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const load = useCallback(() => {
    if (!isAuthenticated) return;
    setError(null);
    fetchMyCoupons()
      .then(setCoupons)
      .catch(() => setError(t("loadCouponsErrorMessage")));
  }, [isAuthenticated, t]);

  useEffect(() => {
    load();
  }, [load]);

  function copyCode(code) {
    navigator.clipboard?.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((prev) => (prev === code ? null : prev)), 1600);
    });
  }

  const sortedCoupons = (coupons || [])
    .slice()
    .sort((a, b) => Number(a.isUsed || a.isExpired || !a.isActive) - Number(b.isUsed || b.isExpired || !b.isActive));

  return (
    <div className="client-coupons-page">
      <ClientHeader />

      <div className="client-coupons-content">
        <h1 className="client-coupons-title">{t("pageTitle")}</h1>

        {!isAuthenticated ? (
          <div className="client-coupons-empty">
            <p>{t("loginPromptCoupons")}</p>
            <button type="button" className="client-coupons-login-button" onClick={openLoginModal}>
              {t("common:logIn")}
            </button>
          </div>
        ) : error ? (
          <div className="client-coupons-empty">
            <p>{error}</p>
            <button type="button" className="client-coupons-login-button" onClick={load}>
              {t("common:tryAgain")}
            </button>
          </div>
        ) : coupons === null ? (
          <div className="client-coupons-empty">
            <p>{t("loadingCouponsMessage")}</p>
          </div>
        ) : sortedCoupons.length === 0 ? (
          <div className="client-coupons-empty">
            <TagIcon />
            <p>{t("emptyCouponsMessage")}</p>
          </div>
        ) : (
          <div className="client-coupons-list">
            {sortedCoupons.map((coupon) => {
              const unavailable = coupon.isUsed || coupon.isExpired || !coupon.isActive;
              const statusLabel = coupon.isUsed
                ? t("statusUsed")
                : coupon.isExpired
                ? t("statusExpired")
                : !coupon.isActive
                ? t("statusInactive")
                : null;

              return (
                <div className={`client-coupon-card ${unavailable ? "unavailable" : ""}`} key={coupon.clientCouponId}>
                  <div className="client-coupon-card-icon">
                    <TagIcon />
                  </div>

                  <div className="client-coupon-card-body">
                    <div className="client-coupon-card-top-row">
                      <span className="client-coupon-code">{coupon.code}</span>
                      {statusLabel && <span className="client-coupon-status-badge">{statusLabel}</span>}
                    </div>
                    <p className="client-coupon-discount">{formatDiscount(coupon, t)}</p>
                    {coupon.minOrderAmount > 0 && (
                      <p className="client-coupon-meta">{t("minOrderLabel", { amount: coupon.minOrderAmount })}</p>
                    )}
                    <p className="client-coupon-meta">{t("expiresLabel", { date: formatDate(coupon.expiresAt) })}</p>
                  </div>

                  {!unavailable && (
                    <button type="button" className="client-coupon-copy-button" onClick={() => copyCode(coupon.code)}>
                      <CopyIcon />
                      {copiedCode === coupon.code ? t("copiedLabel") : t("copyLabel")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
